/* HUENIT LAB 2.0 — Admin: 공용 store (seed 데이터 + localStorage persist).
   CMS 전용(백엔드 없음) 데이터 = 언어/예제분류/가이드. plan.md §7.2. */
import { useState, useEffect } from 'react';

export const uid = (p = 'id') => p + '_' + Math.random().toString(36).slice(2, 9);

/* ---------- 지원 언어 (seed) ---------- */
export const LANG_SEED = [
  { code: 'ko', label: '한국어',  flag: 'KO', default: true },
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'ja', label: '日本語',  flag: 'JA' },
  { code: 'zh', label: '中文',    flag: 'ZH' },
  { code: 'es', label: 'Español', flag: 'ES' },
];

/* ---------- seed: 예제 분류 ---------- */
export const SEED_EX_CATS = [
  { id: 'c_intro', color: '#0d9aa6', label: { ko: '입문', en: 'Beginner', ja: '入門', zh: '入门', es: 'Inicio' } },
  { id: 'c_robot', color: '#e07b1a', label: { ko: '로봇 제어', en: 'Robot', ja: 'ロボット', zh: '机器人', es: 'Robot' } },
  { id: 'c_ai',    color: '#d6488a', label: { ko: 'AI 비전', en: 'AI Vision', ja: 'AIビジョン', zh: 'AI视觉', es: 'Visión IA' } },
];
export const SEED_EX_FILTERS = [
  { id: 'f_easy', label: { ko: '입문', en: 'Easy', ja: '初級', zh: '入门', es: 'Fácil' } },
  { id: 'f_hard', label: { ko: '심화', en: 'Advanced', ja: '上級', zh: '进阶', es: 'Avanzado' } },
  { id: 'f_ai',   label: { ko: 'AI', en: 'AI', ja: 'AI', zh: 'AI', es: 'IA' } },
  { id: 'f_game', label: { ko: '게임', en: 'Game', ja: 'ゲーム', zh: '游戏', es: 'Juego' } },
];

/* ---------- seed: 그림·툴패스(engrave) 예제 분류 ----------
   ★ 코드 예제와 완전히 별도 축이다. 코드 카테고리("입문/로봇 제어/AI 비전")는 드로잉·레이저
   예제에 맞지 않아 억지로 붙이면 오분류가 된다. taxonomy.json 에 engrave_cats /
   engrave_filters 로 따로 게시하고, 앱도 engrave 섹션에서만 이 축을 쓴다. */
export const SEED_ENGRAVE_CATS = [
  { id: 'ec_shape',  color: '#12805c', label: { ko: '기본 도형',   en: 'Shapes',    ja: '基本図形', zh: '基本图形', es: 'Formas' } },
  { id: 'ec_animal', color: '#c2570d', label: { ko: '동물',        en: 'Animals',   ja: '動物',     zh: '动物',     es: 'Animales' } },
  { id: 'ec_letter', color: '#2e6fff', label: { ko: '글자·이름표', en: 'Lettering', ja: '文字',     zh: '文字',     es: 'Letras' } },
  { id: 'ec_deco',   color: '#8b5cf6', label: { ko: '장식·패턴',   en: 'Patterns',  ja: '装飾',     zh: '装饰',     es: 'Patrones' } },
];
export const SEED_ENGRAVE_FILTERS = [
  { id: 'ef_easy',  label: { ko: '입문',   en: 'Easy',    ja: '初級', zh: '入门', es: 'Fácil' } },
  { id: 'ef_line',  label: { ko: '외곽선', en: 'Outline', ja: '輪郭', zh: '轮廓', es: 'Contorno' } },
  { id: 'ef_fill',  label: { ko: '채우기', en: 'Fill',    ja: '塗り', zh: '填充', es: 'Relleno' } },
  { id: 'ef_photo', label: { ko: '사진',   en: 'Photo',   ja: '写真', zh: '照片', es: 'Foto' } },
];

/* ---------- seed: 예제 ---------- */
export const SEED_EXAMPLES = [
  {
    id: uid('ex'), kind: 'block', file: 'cup-mover.hbk',
    catId: 'c_intro', filterIds: ['f_easy'],
    title: { ko: '컵 옮기기 로봇', en: 'Cup Mover Robot' },
    desc:  { ko: '로봇팔로 컵을 옮기는 첫 실습. 블록 5개로 끝나요.', en: 'Move a cup with the robot arm — only 5 blocks.' },
  },
  {
    id: uid('ex'), kind: 'block', file: 'face-led.hbk',
    catId: 'c_ai', filterIds: ['f_ai', 'f_easy'],
    title: { ko: '얼굴 인식 LED', en: 'Face-detect LED' },
    desc:  { ko: 'AI 카메라로 얼굴을 인식하고 LED를 켭니다.', en: 'Detect a face and turn on the LED.' },
  },
  {
    id: uid('ex'), kind: 'python', file: 'yolo_train.py',
    catId: 'c_ai', filterIds: ['f_ai', 'f_hard'],
    title: { ko: 'YOLO 커스텀 학습', en: 'YOLO Custom Training' },
    desc:  { ko: '내 데이터셋으로 YOLO 모델을 학습하는 노트북.', en: 'Train a YOLO model on your own dataset.' },
  },
];

/* ---------- seed: 가이드 (카테고리 → 큰 주제 → 작은 주제) ---------- */
export const blk = (type, data) => ({ id: uid('b'), type, ...data });

export const SEED_GUIDE = [
  {
    id: 'g_intro', color: '#0d9aa6', label: { ko: '시작하기', en: 'Getting Started' },
    big: [
      {
        id: uid('bg'), icon: 'flag', label: { ko: '입문 가이드', en: 'Intro Guide' },
        small: [
          { id: uid('sm'), title: { ko: 'HUENIT Lab 시작하기', en: 'Start HUENIT Lab' },
            content: { ko: [blk('h', { text: '환영합니다' }), blk('p', { text: 'HUENIT Lab에 처음 오셨다면 이 문서부터 읽어보세요.' })] } },
        ],
      },
    ],
  },
  {
    id: 'g_hw', color: '#2e6fff', label: { ko: '제품 하드웨어', en: 'Hardware' },
    big: [
      {
        id: uid('bg'), icon: 'chip', label: { ko: '하드웨어 기본', en: 'Hardware Basics' },
        small: [
          { id: uid('sm'), title: { ko: '제품 구성 & 스펙', en: 'Specs' }, content: { ko: [blk('p', { text: '박스 구성품과 하드웨어 사양을 안내합니다.' })] } },
          { id: uid('sm'), title: { ko: '디바이스 연결 & 페어링', en: 'Pairing' }, content: { ko: [blk('video', { url: 'https://youtube.com/@huenit' }), blk('p', { text: 'USB로 로봇팔과 카메라를 연결하세요.' })] } },
        ],
      },
    ],
  },
];

/* ---------- localStorage 상태 hook ---------- */
export function useLocal(key, initial) {
  const [v, setV] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; }
    catch (e) { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(v)); } catch (e) { /* quota/private mode */ } }, [key, v]);
  return [v, setV];
}

/* 배열 순서 이동 helper */
export const move = (arr, from, to) => {
  const a = [...arr]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a;
};
